import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./RsvpsTab.css";

// Admin/Captain view of who RSVP'd to recent/upcoming events on the
// GroupMe poll/calendar, with a manual wins counter and no-show toggle
// per voter. Captain-or-above on the backend — the tab itself is also
// gated to captain+ in AdminLogin.
//
// Wins UX is built around "host using their phone while a game night
// is in progress": each row has − / + buttons that update the live
// total. Edits are debounced and auto-saved (~800 ms after the last
// click) so the host never has to remember to hit save.
//
// The save-status banner is *always* rendered (never appears/disappears)
// so the +/- buttons below it don't shift up while someone is tapping
// them. "Saved" sticks until the next edit instead of timing out.
//
// Three voter states tracked alongside wins:
//   - default: present, playing, counts for wins.
//   - "Sitting out" (⊘ button): present but not in the win tally.
//     Drops below default voters; +/- buttons disabled. Counts as
//     attended for cross-event participation stats.
//   - "No-show" (× button): didn't come. Drops to the very bottom,
//     name struck through; +/- disabled.
// The two toggles are mutually exclusive — flipping one clears the
// other. All three drafts auto-save through the same debounced POST.
const SAVE_DEBOUNCE_MS = 800;

const RsvpsTab = () => {
  const { getAuthHeaders } = useAuth();

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("");

  const [rsvpsData, setRsvpsData] = useState(null);
  const [rsvpsLoading, setRsvpsLoading] = useState(false);
  const [rsvpsError, setRsvpsError] = useState("");

  // Wins are stored as numbers (not strings) since the only inputs are
  // increment/decrement buttons. Key is the voter's GroupMe user_id.
  const [winsDraft, setWinsDraft] = useState({});
  // No-shows and sitting-out lists are Sets for O(1) toggle / lookup
  // at render time. Persisted to the server as arrays. Mutually
  // exclusive in the UI — setting one clears the other.
  const [noShowsDraft, setNoShowsDraft] = useState(() => new Set());
  const [notPlayingDraft, setNotPlayingDraft] = useState(() => new Set());

  // Save lifecycle: "idle" -> "pending" (debounce window) -> "saving"
  // (POST in flight) -> "saved" (stays until next edit) | "error".
  // Refs let the debounced timer call the latest draft + the latest
  // selected event without re-creating the timer on every state change.
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const saveTimerRef = useRef(null);
  const winsDraftRef = useRef(winsDraft);
  const noShowsDraftRef = useRef(noShowsDraft);
  const notPlayingDraftRef = useRef(notPlayingDraft);
  const selectedEventIdRef = useRef(selectedEventId);
  useEffect(() => {
    winsDraftRef.current = winsDraft;
  }, [winsDraft]);
  useEffect(() => {
    noShowsDraftRef.current = noShowsDraft;
  }, [noShowsDraft]);
  useEffect(() => {
    notPlayingDraftRef.current = notPlayingDraft;
  }, [notPlayingDraft]);
  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
  }, [selectedEventId]);

  // Clear pending timers on unmount so they don't fire against a
  // stale state.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Load the event list once on mount.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setEventsLoading(true);
      setEventsError("");
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(
          `${API_URL}?type=socialEvents&days=14&pastDays=7`,
          { method: "GET", headers }
        );
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (cancelled) return;
        const list = (data.upcomingEvents || [])
          .filter((e) => !!e.poll)
          .sort((a, b) =>
            (b.socialEventId || "").localeCompare(a.socialEventId || "")
          );
        setEvents(list);
        if (list.length > 0 && !selectedEventId) {
          setSelectedEventId(list[0].socialEventId);
        }
      } catch (err) {
        if (!cancelled) {
          setEventsError(err.message || "Failed to load events");
        }
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the RSVPs whenever the selected event changes.
  const loadRsvps = useCallback(
    async (eventId) => {
      if (!eventId) return;
      setRsvpsLoading(true);
      setRsvpsError("");
      setSaveState("idle");
      setSaveError("");
      try {
        const headers = getAuthHeaders();
        const resp = await fetch(
          `${API_URL}?type=eventRsvps&eventId=${encodeURIComponent(eventId)}`,
          { method: "GET", headers }
        );
        const data = await resp.json();
        if (!resp.ok) {
          const parts = [data?.error || data?.message || `HTTP ${resp.status}`];
          if (data?.groupMeStatus) parts.push(`poll: ${data.groupMeStatus}`);
          if (data?.calErrorStatus)
            parts.push(`calendar: ${data.calErrorStatus}`);
          if (data?.details) parts.push(data.details);
          if (data?.calErrorMessage)
            parts.push(`calendar err: ${data.calErrorMessage}`);
          if (data?.pollUrl) parts.push(`pollUrl: ${data.pollUrl}`);
          if (data?.calUrl) parts.push(`calUrl: ${data.calUrl}`);
          throw new Error(parts.join(" — "));
        }
        setRsvpsData(data);
        const initialDraft = {};
        for (const [uid, count] of Object.entries(data.wins || {})) {
          const n = parseInt(count, 10);
          initialDraft[uid] = Number.isFinite(n) && n >= 0 ? n : 0;
        }
        setWinsDraft(initialDraft);
        setNoShowsDraft(new Set(Array.isArray(data.noShows) ? data.noShows : []));
        setNotPlayingDraft(
          new Set(Array.isArray(data.notPlaying) ? data.notPlaying : [])
        );
      } catch (err) {
        setRsvpsError(err.message || "Failed to load RSVPs");
        setRsvpsData(null);
        setWinsDraft({});
        setNoShowsDraft(new Set());
        setNotPlayingDraft(new Set());
      } finally {
        setRsvpsLoading(false);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    if (selectedEventId) loadRsvps(selectedEventId);
  }, [selectedEventId, loadRsvps]);

  // Build the voter list across all poll options. Sort order, top to
  // bottom: active voters, then sitting-out, then no-shows. Alphabetical
  // within each bucket.
  const voterRows = useMemo(() => {
    const poll = rsvpsData?.pollData;
    if (!poll || !Array.isArray(poll.options)) return [];
    const seen = new Map();
    for (const opt of poll.options) {
      const optionLabel = opt.title || opt.text || `Option ${opt.id}`;
      const voterIds = opt.voter_ids || opt.voters || [];
      for (const uid of voterIds) {
        if (!seen.has(uid)) {
          const member = rsvpsData.members?.[uid];
          seen.set(uid, {
            userId: String(uid),
            name: member?.name || `User ${uid}`,
            imageUrl: member?.imageUrl || null,
            option: optionLabel,
          });
        }
      }
    }
    const bucket = (uid) =>
      noShowsDraft.has(uid) ? 2 : notPlayingDraft.has(uid) ? 1 : 0;
    return [...seen.values()].sort((a, b) => {
      const ab = bucket(a.userId);
      const bb = bucket(b.userId);
      if (ab !== bb) return ab - bb;
      return a.name.localeCompare(b.name);
    });
  }, [rsvpsData, noShowsDraft, notPlayingDraft]);

  // POST the current draft (wins + noShows) to the backend. Always
  // reads from refs so a debounced call uses the latest values, even
  // if React state has moved on since the timer was scheduled.
  const flushSave = useCallback(async () => {
    const eventId = selectedEventIdRef.current;
    if (!eventId) return;
    setSaveState("saving");
    setSaveError("");
    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      const winsPayload = {};
      for (const [uid, raw] of Object.entries(winsDraftRef.current)) {
        const n = typeof raw === "number" ? raw : parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 0) winsPayload[uid] = n;
      }
      const noShowsPayload = [...noShowsDraftRef.current];
      const notPlayingPayload = [...notPlayingDraftRef.current];
      const resp = await fetch(API_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type: "eventRsvpUpdate",
          eventId,
          wins: winsPayload,
          noShows: noShowsPayload,
          notPlaying: notPlayingPayload,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(
          data?.error || data?.message || `HTTP ${resp.status}`
        );
      }
      // Mirror what the server stored back so refresh-after-save still
      // sees the right values.
      setRsvpsData((prev) =>
        prev
          ? {
              ...prev,
              wins: data.wins || {},
              noShows: data.noShows || [],
              notPlaying: data.notPlaying || [],
            }
          : prev
      );
      setSaveState("saved");
    } catch (err) {
      setSaveState("error");
      setSaveError(err.message || String(err));
    }
  }, [getAuthHeaders]);

  // Schedule a debounced save after every edit. Multiple +/- clicks
  // (and/or no-show toggles) in quick succession coalesce into one POST.
  const scheduleSave = useCallback(() => {
    setSaveState("pending");
    setSaveError("");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [flushSave]);

  const bumpWins = useCallback(
    (userId, delta) => {
      setWinsDraft((d) => {
        const current = typeof d[userId] === "number" ? d[userId] : 0;
        const next = Math.max(0, Math.min(9999, current + delta));
        if (next === current) return d;
        return { ...d, [userId]: next };
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  const toggleNoShow = useCallback(
    (userId) => {
      setNoShowsDraft((s) => {
        const next = new Set(s);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        return next;
      });
      // Mutually exclusive: marking someone a no-show clears any
      // sitting-out flag they had.
      setNotPlayingDraft((s) => {
        if (!s.has(userId)) return s;
        const next = new Set(s);
        next.delete(userId);
        return next;
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  const toggleNotPlaying = useCallback(
    (userId) => {
      setNotPlayingDraft((s) => {
        const next = new Set(s);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        return next;
      });
      // Mutually exclusive with no-show too.
      setNoShowsDraft((s) => {
        if (!s.has(userId)) return s;
        const next = new Set(s);
        next.delete(userId);
        return next;
      });
      scheduleSave();
    },
    [scheduleSave]
  );

  const handleManualSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    flushSave();
  }, [flushSave]);

  const saveStatusLabel = (() => {
    switch (saveState) {
      case "pending":
        return "Pending save…";
      case "saving":
        return "Saving…";
      case "saved":
        return "Saved ✓";
      case "error":
        return `Save failed: ${saveError}`;
      default:
        // Idle placeholder — single short label so the height of the
        // status box matches every other state and never shifts the
        // buttons below it.
        return "Auto-save on";
    }
  })();

  return (
    <div className="rsvps-tab">
      <h2 className="rsvps-tab__title">RSVPs &amp; Wins</h2>

      {eventsLoading && <div className="rsvps-tab__info">Loading events…</div>}
      {eventsError && (
        <div className="rsvps-tab__error">Error: {eventsError}</div>
      )}

      {!eventsLoading && !eventsError && events.length === 0 && (
        <div className="rsvps-tab__info">
          No events with a GroupMe poll in the last 7 days or next 14 days.
        </div>
      )}

      {events.length > 0 && (
        <div className="rsvps-tab__picker">
          <label htmlFor="rsvps-event-select">Event:</label>
          <select
            id="rsvps-event-select"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
          >
            {events.map((ev) => (
              <option key={ev.socialEventId} value={ev.socialEventId}>
                {ev.socialEventId} — {ev.title || "(no title)"}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rsvps-tab__btn"
            onClick={() => loadRsvps(selectedEventId)}
            disabled={!selectedEventId || rsvpsLoading}
          >
            Refresh
          </button>
        </div>
      )}

      {rsvpsLoading && <div className="rsvps-tab__info">Loading RSVPs…</div>}
      {rsvpsError && (
        <div className="rsvps-tab__error">Error: {rsvpsError}</div>
      )}
      {rsvpsData?.warning && (
        <div className="rsvps-tab__info">{rsvpsData.warning}</div>
      )}

      {voterRows.length > 0 && (
        <>
          {/* Static instructions banner. Never changes, so it can't shift
              the buttons below it. */}
          <div className="rsvps-tab__instructions">
            Tap − or + to record wins · ⊘ to sit out · × no-show
          </div>
          {/* Save-status banner. Always rendered with a placeholder so
              it doesn't appear/disappear between idle and active states. */}
          <div
            className={
              saveState === "error"
                ? "rsvps-tab__error"
                : "rsvps-tab__save-status"
            }
            aria-live="polite"
          >
            {saveStatusLabel}
            {saveState === "error" && (
              <button
                type="button"
                className="rsvps-tab__btn rsvps-tab__btn--primary"
                style={{ marginLeft: 8 }}
                onClick={handleManualSave}
              >
                Retry save
              </button>
            )}
          </div>
          <table className="rsvps-tab__table">
            <thead>
              <tr>
                <th className="rsvps-tab__status-col"></th>
                <th>Voter</th>
                <th>RSVP</th>
                <th className="rsvps-tab__wins-col">Wins</th>
              </tr>
            </thead>
            <tbody>
              {voterRows.map((row) => {
                const count =
                  typeof winsDraft[row.userId] === "number"
                    ? winsDraft[row.userId]
                    : 0;
                const isNoShow = noShowsDraft.has(row.userId);
                const isSittingOut = notPlayingDraft.has(row.userId);
                const winsDisabled = isNoShow || isSittingOut;
                const rowClass = isNoShow
                  ? "rsvps-tab__row--noshow"
                  : isSittingOut
                  ? "rsvps-tab__row--sittingout"
                  : "";
                return (
                  <tr key={row.userId} className={rowClass}>
                    <td className="rsvps-tab__status-col">
                      <button
                        type="button"
                        className={
                          isSittingOut
                            ? "rsvps-tab__status-btn rsvps-tab__status-btn--sittingout"
                            : "rsvps-tab__status-btn"
                        }
                        onClick={() => toggleNotPlaying(row.userId)}
                        aria-label={
                          isSittingOut
                            ? `Bring ${row.name} back into the win tally`
                            : `Sit ${row.name} out (present, not in wins)`
                        }
                        title={
                          isSittingOut
                            ? "Sitting out — tap to bring back"
                            : "Sit out (present, not counting wins)"
                        }
                      >
                        ⊘
                      </button>
                      <button
                        type="button"
                        className={
                          isNoShow
                            ? "rsvps-tab__status-btn rsvps-tab__status-btn--noshow"
                            : "rsvps-tab__status-btn"
                        }
                        onClick={() => toggleNoShow(row.userId)}
                        aria-label={
                          isNoShow
                            ? `Restore ${row.name} (undo no-show)`
                            : `Mark ${row.name} as no-show`
                        }
                        title={
                          isNoShow ? "Undo no-show" : "Mark as no-show"
                        }
                      >
                        ×
                      </button>
                    </td>
                    <td className="rsvps-tab__name-cell">{row.name}</td>
                    <td>{row.option}</td>
                    <td className="rsvps-tab__wins-col">
                      <div className="rsvps-tab__wins-stepper">
                        <button
                          type="button"
                          className="rsvps-tab__step-btn"
                          onClick={() => bumpWins(row.userId, -1)}
                          disabled={count === 0 || winsDisabled}
                          aria-label={`Decrease wins for ${row.name}`}
                        >
                          −
                        </button>
                        <span
                          className="rsvps-tab__wins-count"
                          aria-live="polite"
                          aria-label={`${count} wins for ${row.name}`}
                        >
                          {count}
                        </span>
                        <button
                          type="button"
                          className="rsvps-tab__step-btn"
                          onClick={() => bumpWins(row.userId, 1)}
                          disabled={count >= 9999 || winsDisabled}
                          aria-label={`Increase wins for ${row.name}`}
                        >
                          +
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {!rsvpsLoading && rsvpsData && voterRows.length === 0 && (
        <div className="rsvps-tab__info">
          No votes recorded on this event's GroupMe poll yet.
        </div>
      )}
    </div>
  );
};

export default RsvpsTab;
